import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { NetezzaLinkedService, netezzaSecurityLevelTypes } from 'app/model/linkedservice/netezza.linkedservice';
import { akvToResource, akvToModel, secretBaseToPasswordOrAKV, passwordOrAKVtoSecretBase } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from '../../../shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { Constants } from '../../../shared/shared-constants';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext } from 'shell-models-lib';
import { CONNECTOR_VERSION20, GlobalVariables } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';
const netezzaReservedKeys = ['server', 'pwd', 'port', 'database', 'uid', 'encryptedCredential', 'securityLevel'];

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(NetezzaLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: NetezzaLinkedService) => {
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
                        // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                        // won't cover fully by json for now
                        if (ls.password instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                server: '*',
                port: '*',
                database: '*',
                uid: '*',
                securityLevel: '*',
                pwd: 'secret',
                encryptedCredential: '*'
            }
        }
    }
})
export class NetezzaLinkedServiceAdapter extends LinkedServiceAdapter<NetezzaLinkedService> {
    protected modelConstructor(): new () => NetezzaLinkedService {
        return NetezzaLinkedService;
    }

    protected model(): IModel {
        return NetezzaLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: NetezzaLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.NetezzaLinkedService;
        properties.type = Metamodel.modelFor(NetezzaLinkedService).name;
        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            const results: Record<string, string | boolean | number> = {};
            linkedService.additionalFields?.forEach((pair) => {
                if (pair.value != null && !netezzaReservedKeys.includes(pair.key)) {
                    results[pair.key] = pair.value;
                }
            });

            properties.typeProperties = {
                ...results,
                server: linkedService.server,
                port: linkedService.port,
                pwd: passwordOrAKVtoSecretBase(linkedService.password, this.context),
                uid: linkedService.userName,
                database: linkedService.databaseName,
                securityLevel: linkedService.securityLevel,
            };
            return linkedServiceResource;
        }
        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('Server', linkedService.server || '');
            builder.add('Port', linkedService.port != null ? linkedService.port.toString() : '');
            builder.add('Database', linkedService.databaseName || '');
            builder.add('UID', linkedService.userName || '');
            if (linkedService.password && !(linkedService.password instanceof AzureKeyVault)) {
                builder.add('PWD', linkedService.password);
            }
            builder.add('SecurityLevel', linkedService.securityLevel || '');
            if (linkedService.securityLevel !== netezzaSecurityLevelTypes.OnlyUnSecured) {
                builder.add('CaCertFile', linkedService.caCertFile || '');
            }
            if (GlobalVariables.enableNetezzaPrefetch) {
                if (linkedService.prefetch != null) {
                    builder.add('Prefetch', linkedService.prefetch.toString());
                }
                if (linkedService.socket != null) {
                    builder.add('Socket', linkedService.socket.toString());
                }
            }
            if (linkedService.additionalFields) {
                linkedService.additionalFields.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });
            }
            connectionString = linkedService.server === Constants.maskedCredential &&
                linkedService.port === undefined &&
                linkedService.databaseName === Constants.maskedCredential &&
                linkedService.userName === Constants.maskedCredential &&
                linkedService.password === Constants.maskedCredential &&
                linkedService.securityLevel === undefined &&
                linkedService.prefetch === undefined &&
                linkedService.socket === undefined &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.caCertFile === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }
        properties.typeProperties = {
            connectionString,
            pwd: !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: NetezzaLinkedService): Promise<NetezzaLinkedService> {
        const properties = linkedServiceResource.properties as Model.NetezzaLinkedService;
        const typeProperties = properties.typeProperties;
        linkedService.server = undefined;
        linkedService.port = undefined;
        linkedService.databaseName = undefined;
        linkedService.userName = undefined;
        linkedService.password = undefined;
        linkedService.securityLevel = undefined;
        linkedService.caCertFile = undefined;
        linkedService.prefetch = undefined;
        linkedService.socket = undefined;
        linkedService.version = properties.version === CONNECTOR_VERSION20 ? driverVersions.v2 : driverVersions.v1;
        const isV2 = properties.version === CONNECTOR_VERSION20;
        linkedService.isLegacyConnector = !isV2;
        if (properties?.version === CONNECTOR_VERSION20) {
            linkedService.server = typeProperties.server;
            linkedService.port = typeProperties.port;
            linkedService.databaseName = typeProperties.database;
            linkedService.userName = typeProperties.uid;
            linkedService.password = secretBaseToPasswordOrAKV(typeProperties.pwd, true, this.context);
            linkedService.securityLevel = typeProperties.securityLevel;

            linkedService.additionalFields = [];
            for (const [key, value] of Object.entries(typeProperties)) {
                if (!netezzaReservedKeys.includes(key)) {
                    linkedService.additionalFields.push({
                        key,
                        value
                    });
                }
            }
        } else if (isString(typeProperties.connectionString)) {
            const builder = new DbConnectionStringBuilderService();
            builder.parse(typeProperties.connectionString as string);

            linkedService.server = builder.getValue('Server', false) || '';
            linkedService.port = parseInt(builder.getValue('Port', false), 10) || undefined;
            linkedService.databaseName = builder.getValue('Database', false) || '';
            linkedService.userName = builder.getValue('UID', false) || '';
            linkedService.password = Constants.maskedCredential;
            linkedService.securityLevel = builder.getValue('SecurityLevel', false) || '';
            linkedService.caCertFile = builder.getValue('CaCertFile', false) || '';
            if (GlobalVariables.enableNetezzaPrefetch) {
                try {
                    linkedService.prefetch = parseInt(builder.getValue('Prefetch', false), 10);
                    linkedService.socket = parseInt(builder.getValue('Socket', false), 10);
                } catch (error) {
                    // swallow error
                }
                linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Server', 'Port', 'Database', 'UID', 'PWD', 'SecurityLevel', 'CacertFile', 'Prefetch', 'Socket']);
            } else {
                linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Server', 'Port', 'Database', 'UID', 'PWD', 'SecurityLevel', 'CacertFile']);
            }
            if (typeProperties.pwd) {
                linkedService.password = secretBaseToPasswordOrAKV(typeProperties.pwd, false, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(typeProperties.connectionString)) {
            linkedService.server = Constants.maskedCredential;
            linkedService.databaseName = Constants.maskedCredential;
            linkedService.userName = Constants.maskedCredential;
            linkedService.password = Constants.maskedCredential;
            linkedService.port = undefined;
            linkedService.securityLevel = undefined;
            linkedService.caCertFile = Constants.maskedCredential;
            linkedService.prefetch = undefined;
            linkedService.socket = undefined;
            if (typeProperties.pwd) {
                linkedService.password = secretBaseToPasswordOrAKV(typeProperties.pwd, false, this.context);
            }
        } else if (typeProperties.connectionString
            && (typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.referedAKV = akvToModel(typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
            linkedService.enableAKV = true;
        }
        return linkedService;
    }
}
